// Centralized service for hypothesis database operations
import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '@/integrations/supabase/types';
import type {
  Hypothesis,
  NewHypothesisInput,
  UpdateHypothesisInput,
} from '@/types/hypotheses';
import { handleError } from '@/utils/errorHandling';

type SupabaseClientType = SupabaseClient<Database>;

export class HypothesesService {
  /**
   * List hypotheses for a user, optionally filtered by product
   */
  static async list(
    supabase: SupabaseClientType,
    productId?: string,
    userId?: string
  ): Promise<Hypothesis[]> {
    try {
      let query = supabase
        .from('hypotheses')
        .select('*')
        .order('updated_at', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      }

      // If filtering by product, we need to join with product_hypotheses table
      if (productId) {
        query = supabase
          .from('hypotheses')
          .select(
            `
            *,
            product_hypotheses!inner(product_id)
          `
          )
          .eq('product_hypotheses.product_id', productId)
          .order('updated_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data || []) as Hypothesis[];
    } catch (error) {
      handleError(error, {
        action: 'fetch hypotheses',
        logToConsole: true,
        showToast: false, // Let calling code handle toast
      });
      throw error;
    }
  }

  /**
   * Get a single hypothesis by ID
   */
  static async get(
    supabase: SupabaseClientType,
    id: string,
    userId: string
  ): Promise<Hypothesis | null> {
    try {
      const { data, error } = await supabase
        .from('hypotheses')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      return data as Hypothesis | null;
    } catch (error) {
      handleError(error, {
        action: 'fetch hypothesis',
        logToConsole: true,
        showToast: false,
      });
      throw error;
    }
  }

  /**
   * Create a new hypothesis
   */
  static async create(
    supabase: SupabaseClientType,
    userId: string,
    input: NewHypothesisInput
  ): Promise<Hypothesis> {
    try {
      const payload = {
        user_id: userId,
        title: input.title,
        description: input.description || null,
      };

      const { data, error } = await supabase
        .from('hypotheses')
        .insert(payload)
        .select('*')
        .single();

      if (error) throw error;

      return data as Hypothesis;
    } catch (error) {
      handleError(error, {
        action: 'create hypothesis',
        logToConsole: true,
        showToast: false,
      });
      throw error;
    }
  }

  /**
   * Update an existing hypothesis
   */
  static async update(
    supabase: SupabaseClientType,
    userId: string,
    input: UpdateHypothesisInput
  ): Promise<Hypothesis> {
    try {
      const { id, ...updateData } = input;

      // Update the hypothesis
      const { data, error } = await supabase
        .from('hypotheses')
        .update({
          ...updateData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', userId)
        .select('*')
        .single();

      if (error) throw error;

      return data as Hypothesis;
    } catch (error) {
      handleError(error, {
        action: 'update hypothesis',
        logToConsole: true,
        showToast: false,
      });
      throw error;
    }
  }

  /**
   * Delete a hypothesis
   */
  static async remove(
    supabase: SupabaseClientType,
    userId: string,
    id: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('hypotheses')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;
    } catch (error) {
      handleError(error, {
        action: 'delete hypothesis',
        logToConsole: true,
        showToast: false,
      });
      throw error;
    }
  }

  /**
   * Search hypotheses by title and description
   */
  static async search(
    supabase: SupabaseClientType,
    userId: string,
    searchQuery: string,
    limit = 10
  ): Promise<Hypothesis[]> {
    try {
      if (!searchQuery || searchQuery.trim().length === 0) {
        return [];
      }

      const { data, error } = await supabase
        .from('hypotheses')
        .select('*')
        .eq('user_id', userId)
        .or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []) as Hypothesis[];
    } catch (error) {
      handleError(error, {
        action: 'search hypotheses',
        logToConsole: true,
        showToast: false,
      });
      throw error;
    }
  }
}
